import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import { getCapabilities } from "@/api/endpoints/organization";
import { LANGUAGE, Language, SOURCE, Source } from "@/constants";
import { Capabilities } from "@/types/Capabilities";

const DEFAULT_CAPABILITIES = {
  languages: [],
  sources: [],
  sources_audio: [],
  sources_news: [],
  sources_social: [],
  industries: [],
  markets: [],
};

const PREFERRED_SOURCE_OURDER: Source[] = [
  SOURCE.PODCAST,
  SOURCE.RADIO,
  SOURCE.TIKTOK,
  SOURCE.TV,
  SOURCE.WEB_TV,
  SOURCE.YOUTUBE,
  SOURCE.YOUTUBE_SHORTS,
  SOURCE.FACEBOOK,
  SOURCE.INSTAGRAM,
  SOURCE.TWITTER,
  SOURCE.LINKEDIN,
  SOURCE.NEWS,
];

const PREFERRED_LANGUAGE_ORDER: Language[] = [
  LANGUAGE.SWEDISH,
  LANGUAGE.ENGLISH,
  LANGUAGE.NORWEGIAN,
  LANGUAGE.FINNISH,
  LANGUAGE.DANISH,
  LANGUAGE.GERMAN,
  LANGUAGE.SPANISH,
  LANGUAGE.FRENCH,
  LANGUAGE.ITALIAN,
  LANGUAGE.DUTCH,
  LANGUAGE.PORTUGUESE,
];

export type CapabilitiesContextType = {
  capabilities: Capabilities;
};

const CapabilitiesContext = createContext<CapabilitiesContextType>({
  capabilities: DEFAULT_CAPABILITIES,
});

export const useCapabilities = () => useContext(CapabilitiesContext);

const orderLanguages = (languages: Language[]): Language[] => {
  return PREFERRED_LANGUAGE_ORDER.filter((language) =>
    languages.includes(language)
  );
};

const orderSources = (sources: Source[]): Source[] => {
  return PREFERRED_SOURCE_OURDER.filter((source) => sources.includes(source));
};

export const CapabilitiesProvider: React.FunctionComponent<{
  children: ReactNode;
}> = ({ children }) => {
  const { orgUuid } = useParams<{ orgUuid: string }>();
  const [capabilities, setCapabilities] =
    useState<Capabilities>(DEFAULT_CAPABILITIES);

  useEffect(() => {
    if (!orgUuid) return;
    let isMounted = true;
    const fetchCapabilities = async () => {
      try {
        const res = await getCapabilities(orgUuid);
        if (isMounted && res.status === 200 && res.data) {
          const orderedLanguages = orderLanguages(res.data.languages);
          const orderedSources = orderSources(res.data.sources);
          setCapabilities({
            ...res.data,
            languages: orderedLanguages,
            sources: orderedSources,
          });
        }
      } catch {
        // handle error?
      }
    };

    fetchCapabilities();

    return () => {
      isMounted = false;
    };
  }, [orgUuid]);

  const contextValue = useMemo(() => ({ capabilities }), [capabilities]);

  return (
    <CapabilitiesContext.Provider value={contextValue}>
      {children}
    </CapabilitiesContext.Provider>
  );
};
export default CapabilitiesContext;
